import { Editable, ReactEditor, RenderLeafProps, Slate, withReact } from 'slate-react';
import { createEditor, NodeEntry, Text, Range, Descendant, Transforms, Node, Editor } from 'slate';
import { withHistory, HistoryEditor } from 'slate-history';
import { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import has from 'lodash/has';
import styles from './richTextEditor.module.less';
import {
    RichTextEditorProps,
    RichTextEditorRef,
} from '@copilot/common/components/editors/richTextEditor/types/richTextEditorTypes';
import { normalizeLineEndings } from './richTextEditorHelpers';

type CustomEditor = Editor & ReactEditor & HistoryEditor;
/**
 * A RichTextEditor component with support for highlighting specific tokens.
 */
export const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(
    (
        {
            tokens,
            value,
            placeholder,
            onChange,
            onFocus,
            onBlur,
            disabled,
            disabledValue,
            size = 'default',
            multiline = false,
            resize = false,
            bordered = true,
            isEditing = true,
            padded = true,
        }: RichTextEditorProps,
        ref
    ) => {
        const [editor] = useState(() =>
            !multiline
                ? withSingleLine(withHistory(withReact(createEditor())))
                : withHistory(withReact(createEditor()))
        );

        /**
         * If the value changes, we need to reset the editor's content,
         * only if it is different from the current content.
         */
        useEffect(() => {
            const currentValue = serializePlain(editor.children);
            const expectedValue = disabled ? disabledValue : value;
            if (currentValue === expectedValue) return;
            Transforms.removeNodes(editor, {
                at: { anchor: Editor.start(editor, []), focus: Editor.end(editor, []) },
            });
            Transforms.insertNodes(editor, deserializePlain(expectedValue), {
                at: [0],
                select: true,
            });
        }, [value, disabled, disabledValue, editor]);

        /**
         * Deserializes a string into a Slate-compatible paragraph format.
         * @param plainStr
         */
        function deserializePlain(plainStr: string | undefined): Descendant[] {
            const paragraphs = normalizeLineEndings(plainStr)?.split('\n') ?? [''];
            return paragraphs.map((p) => ({
                type: 'paragraph',
                children: [{ text: p }],
            }));
        }

        /**
         * Serializes a Slate paragraphs into a string.
         * @param descendants
         */
        function serializePlain(descendants: Descendant[]): string {
            return descendants.map((node) => Node.string(node)).join('\n');
        }

        function isToken(leaf: Text): boolean {
            return has(leaf, 'token') && leaf.token == true;
        }

        /**
         * RenderLeaf component to display the text with a specific style.
         * @param leafProps
         * @constructor
         */
        function Leaf(leafProps: RenderLeafProps) {
            const { attributes, children, leaf } = leafProps;
            if (isToken(leaf)) {
                return (
                    <span {...attributes} className={styles['leaf-token']}>
                        {children}
                    </span>
                );
            }
            return (
                <span {...attributes} className={disabled ? styles['leaf-disabled'] : ''}>
                    {children}
                </span>
            );
        }

        useImperativeHandle(ref, () => ({
            focus: () => {
                if (!ReactEditor.isFocused(editor)) {
                    ReactEditor.focus(editor);
                    const end = Editor.end(editor, []);
                    Transforms.select(editor, end);
                }
            },
            insertAtSelection: (token: string): string => {
                // Store the current selection before focusing
                const currentSelection = editor.selection;

                if (!ReactEditor.isFocused(editor)) {
                    ReactEditor.focus(editor);

                    // If there was a selection, restore it; otherwise go to the end
                    if (currentSelection) {
                        Transforms.select(editor, currentSelection);
                    } else {
                        const end = Editor.end(editor, []);
                        Transforms.select(editor, end);
                    }
                }

                Transforms.insertText(editor, token);
                return serializePlain(editor.children);
            },
        }));

        /**
         * Decorate function to highlight specific tokens in the editor.
         */
        function decorate([node, path]: NodeEntry) {
            const escapedTokens = tokens.map((token) =>
                token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            );
            const pattern = new RegExp(`(${escapedTokens.join('|')})`, 'g');
            const ranges: Range[] = [];

            if (!Text.isText(node)) {
                return ranges;
            }

            const { text } = node;
            let match;
            while ((match = pattern.exec(text)) !== null) {
                const start = match.index;
                const end = start + match[0].length;
                ranges.push({
                    anchor: { path, offset: start },
                    focus: { path, offset: end },
                    token: true,
                });
            }
            return ranges;
        }

        /**
         * Handle keyboard events for undo/redo functionality
         */
        function handleKeyDown(event: React.KeyboardEvent) {
            if (disabled || !isEditing) return;

            const { key, ctrlKey, metaKey, shiftKey } = event;
            const isModifierPressed = ctrlKey || metaKey;

            if (isModifierPressed && key === 'z') {
                event.preventDefault();
                if (shiftKey) {
                    // Redo: Ctrl+Shift+Z or Cmd+Shift+Z
                    HistoryEditor.redo(editor as CustomEditor);
                } else {
                    // Undo: Ctrl+Z or Cmd+Z
                    HistoryEditor.undo(editor as CustomEditor);
                }
            } else if (isModifierPressed && key === 'y') {
                // Redo: Ctrl+Y or Cmd+Y
                event.preventDefault();
                HistoryEditor.redo(editor as CustomEditor);
            }
        }

        return (
            <div
                className={`${styles.editorContainer} ${resize ? styles.resizable : ''} ${bordered ? styles.bordered : ''} ${disabled ? styles.disabled : ''} ${padded ? styles.padded : ''}`}
            >
                <Slate
                    editor={editor}
                    initialValue={deserializePlain(disabled ? disabledValue : value)}
                    onValueChange={(descendants: Descendant[]) => {
                        if (!disabled && isEditing) {
                            onChange?.(serializePlain(descendants));
                        }
                    }}
                >
                    <Editable
                        className={`${styles.editable} ${styles[`size-${size}`]} ${!isEditing ? styles.readOnly : ''}`}
                        placeholder={placeholder}
                        readOnly={disabled ?? !isEditing}
                        decorate={decorate}
                        renderLeaf={(leadProps) => <Leaf {...leadProps} />}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onKeyDown={handleKeyDown}
                        disableDefaultStyles
                    />
                </Slate>
            </div>
        );
    }
);

/**
 * A Slate plugin that allows only a single line of text in the editor.
 * This means that the editor will not allow line breaks and will remove any nodes outside the first line.
 * @param editor
 */
function withSingleLine(editor: Editor) {
    const { normalizeNode } = editor;
    editor.insertBreak = () => null;
    editor.normalizeNode = (entry: NodeEntry) => {
        if (editor.children.length > 1) {
            Transforms.removeNodes(editor, {
                at: [],
                mode: 'highest',
                match: (_: unknown, path: number[]) => path[0] > 0,
            });
        }
        normalizeNode(entry);
    };

    return editor;
}
